import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Category, CreateCategoryRequest, UpdateCategoryRequest } from '../types/index';
import apiService from '../services/api';
import PageHeader from './ui/PageHeader';
import Badge from './ui/Badge';
import Button from './ui/Button';
import Modal from './ui/Modal';
import EmptyState from './ui/EmptyState';
import { PlusIcon, TagIcon } from '@heroicons/react/24/outline';

const Categories: React.FC = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [contractors, setContractors] = useState<{ id: string; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<CreateCategoryRequest>({
    name: '',
    description: '',
    icon: '📋',
    color: '#3B82F6',
    departmentId: '',
    contractorId: '',
  });

  useEffect(() => {
    fetchCategories();
    fetchDepartments();
    fetchContractors();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiService.getDepartments();
      if (response.success && response.data) setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchContractors = async () => {
    try {
      const response = await apiService.get<{ id: string; name: string }[]>('/platform/contractors');
      if (response.success && response.data) setContractors(response.data);
    } catch (error) {
      console.error('Error fetching contractors:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await apiService.createCategory({
        ...formData,
        departmentId: formData.departmentId || undefined,
        contractorId: formData.contractorId || undefined,
      });
      if (response.success && response.data) {
        setCategories(prev => [...prev, response.data!]);
        setShowCreateModal(false);
        setFormData({ name: '', description: '', icon: '📋', color: '#3B82F6', departmentId: '', contractorId: '' });
      }
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory) return;

    try {
      const updateData: UpdateCategoryRequest = {
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
        color: formData.color,
        departmentId: formData.departmentId || undefined,
        contractorId: formData.contractorId || null,
      };

      const response = await apiService.updateCategory(selectedCategory.id, updateData);
      if (response.success && response.data) {
        setCategories(prev => prev.map(cat =>
          cat.id === selectedCategory.id ? response.data! : cat
        ));
        setShowEditModal(false);
        setSelectedCategory(null);
        setFormData({ name: '', description: '', icon: '📋', color: '#3B82F6', departmentId: '', contractorId: '' });
      }
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      const response = await apiService.deleteCategory(id);
      if (response.success) {
        setCategories(prev => prev.filter(cat => cat.id !== id));
      }
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const openEditModal = (category: Category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      icon: category.icon,
      color: category.color,
      departmentId: category.departmentId || '',
      contractorId: category.contractorId || '',
    });
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setFormData({ name: '', description: '', icon: '📋', color: '#3B82F6', departmentId: '', contractorId: '' });
    setShowCreateModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Categories"
        subtitle="Manage issue categories and their properties"
        actions={
          <Button variant="primary" icon={PlusIcon} onClick={openCreateModal}>
            Add Category
          </Button>
        }
      />

      {categories.length === 0 ? (
        <EmptyState icon={TagIcon} title="No categories yet" description="Add a category to start classifying reports." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <div key={category.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-full text-lg text-white"
                    style={{ backgroundColor: category.color || '#3B82F6' }}
                  >
                    {category.icon || '📋'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{category.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{category.description}</p>
                  </div>
                </div>
                <Badge tone={category.isActive ? 'success' : 'danger'}>{category.isActive ? 'Active' : 'Inactive'}</Badge>
              </div>

              {category.contractorName ? (
                <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 inline-block">
                  Contractor-exclusive — {category.contractorName}
                </div>
              ) : category.departmentName ? (
                <div className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                  Department: {category.departmentName}
                </div>
              ) : null}

              <div className="flex gap-2">
                <Button size="sm" variant="secondary" className="flex-1 justify-center" onClick={() => openEditModal(category)}>
                  Edit
                </Button>
                <Button size="sm" variant="danger-ghost" className="flex-1 justify-center" onClick={() => handleDelete(category.id)}>
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Category"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button type="submit" form="create-category-form" variant="primary">
              Create
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreate} id="create-category-form" className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="input-field mt-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} className="input-field mt-1" rows={3} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Icon</label>
            <input type="text" name="icon" value={formData.icon} onChange={handleInputChange} className="input-field mt-1" placeholder="📋" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Color</label>
            <input type="color" name="color" value={formData.color} onChange={handleInputChange} className="input-field mt-1 h-10" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
            <select name="departmentId" value={formData.departmentId || ''} onChange={handleInputChange} className="input-field mt-1">
              <option value="">No department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Awarded to contractor</label>
            <select name="contractorId" value={formData.contractorId || ''} onChange={handleInputChange} className="input-field mt-1">
              <option value="">Not contracted out</option>
              {contractors.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              If set, only this contractor's technicians will ever be assigned reports in this category — municipal staff are excluded.
            </p>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={showEditModal && !!selectedCategory}
        onClose={() => setShowEditModal(false)}
        title="Edit Category"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button type="submit" form="edit-category-form" variant="primary">
              Update
            </Button>
          </>
        }
      >
        <form onSubmit={handleEdit} id="edit-category-form" className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="input-field mt-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} className="input-field mt-1" rows={3} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Icon</label>
            <input type="text" name="icon" value={formData.icon} onChange={handleInputChange} className="input-field mt-1" placeholder="📋" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Color</label>
            <input type="color" name="color" value={formData.color} onChange={handleInputChange} className="input-field mt-1 h-10" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Department</label>
            <select name="departmentId" value={formData.departmentId || ''} onChange={handleInputChange} className="input-field mt-1">
              <option value="">No department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Awarded to contractor</label>
            <select name="contractorId" value={formData.contractorId || ''} onChange={handleInputChange} className="input-field mt-1">
              <option value="">Not contracted out</option>
              {contractors.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              If set, only this contractor's technicians will ever be assigned reports in this category — municipal staff are excluded.
            </p>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Categories;

